import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";

import {
  CreateMeterManufacturerPayload,
  CreateMeterModelPayload,
  UpdateMeterManufacturerPayload,
} from "./types";
import { mxManufacturerApi } from "./api";
import { toast } from "@shared/ui/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const useMeterManufacturers = (params: {
  remote_utility_id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterManufacturers(params),
    () => mxManufacturerApi.getMeterManufacturers(params)
  );
};

export const useMeterModels = (params: {
  remote_utility_id: any;
  meter_type: any;
  searchData: any;
  manufacturer: any;
  is_active: any;
  page: any;
  limit: any;
}) => {
  // Enable query when we have remote_utility_id
  const isEnabled = !!params.remote_utility_id;

  // Always pass meter_type, even if it's an empty string
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterModels(params),
    () => mxManufacturerApi.getMeterModels(params), // Pass all params including empty meter_type
    {
      enabled: isEnabled,
    }
  );
};

export const useMeterModelDetail = (params: {
  remote_utility_id: any;
  meter_model_id: string;
}) => {
  const isEnabled = !!params.remote_utility_id && !!params.meter_model_id;

  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterModelDetail(params),
    () => mxManufacturerApi.getMeterModelDetail(params),
    {
      enabled: isEnabled,
    }
  );
};

export const useMeterList = (params: {
  remote_utility_id: any;
  meter_model_id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterList(params),
    () => mxManufacturerApi.getMeterList(params)
  );
};

export const useMeterType = () => {
  return useSmartQuery(QueryKeyFactory.module.mx.manufacturer.meterType(), () =>
    mxManufacturerApi.getMeterType()
  );
};

export const useManufacturerType = (params: {
  remote_utility_id: any;
  is_dropdown: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.manufacturerType(params),
    () => mxManufacturerApi.getManufacturerType(params)
  );
};

export const useMeterModelSummary = (params: { remote_utility_id: string }) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterModelSummary(params),
    () => mxManufacturerApi.getMeterModelSummary(params)
  );
};

// POST/PUT Hooks
export const useCreateMeterManufacturer = () => {
  const utiltiyID = getRemoteUtilityId();
  return useSmartMutation(
    (payload: CreateMeterManufacturerPayload) =>
      mxManufacturerApi.createMeterManufacturer(payload),
    {
      onSuccess: () => {
toast({
  title: "Success",
  description: "Meter manufacturer created successfully",
  variant: "default",
});
        queryClient.refetchQueries({
          queryKey: ["manufacturer", "meterModelSummary"],
        });

        queryClient.refetchQueries({
          queryKey: ["manufacturer", "meterManufacturers"],
        });
      },
    }
  );
};

export const useCreateMeterModel = () => {
  return useSmartMutation(
    (payload: CreateMeterModelPayload) =>
      mxManufacturerApi.createMeterModel(payload),
    {
      onSuccess: () => {
toast({
  title: "Success",
  description: "Meter model created successfully",
  variant: "default",
});
        queryClient.refetchQueries({
          queryKey: ["manufacturer", "meterModels"],
        });

        queryClient.refetchQueries({
          queryKey: ["manufacturer", "meterModelSummary"],
        });
      },
    }
  );
};

export const useUpdateMeterManufacturer = () => {
  return useSmartMutation(
    ({
      manufacturerId,
      payload,
    }: {
      manufacturerId: string;
      payload: UpdateMeterManufacturerPayload;
    }) => mxManufacturerApi.updateMeterManufacturer(manufacturerId, payload)
  );
};

export const useDeleteMeterModel = () => {
  return useSmartMutation(
    async (meterModelId: string) => {
      return mxManufacturerApi.delete(meterModelId);
    },
    {
      onMutate: async (meterModelId: string) => {
        const remote_utility_id = getRemoteUtilityId(); // however you fetch this
        return {
          invalidateParams: {
            remote_utility_id,
            meter_model_id: meterModelId,
          },
        };
      },
      onSuccess: (_data, _meterModelId, context) => {
        const invalidateParams = (context as any)?.invalidateParams;

        if (invalidateParams) {
          queryClient.refetchQueries({
            queryKey: ["manufacturer", "meterModels"],
          });
        }

toast({
  title: "Success",
  description: "Meter model deleted successfully!",
  variant: "default",
});
      },
    }
  );
};

export const useUpdateMeterModel = () => {
  return useSmartMutation(
    ({ id, payload }: { id: any; payload: CreateMeterModelPayload }) =>
      mxManufacturerApi.updateMeterModel(id, payload),
    {
      onSuccess: () => {
toast({
  title: "Success",
  description: "Meter model updated successfully",
  variant: "default",
});
        queryClient.refetchQueries({
          queryKey: ["manufacturer", "meterModelDetail"],
        });
        queryClient.refetchQueries({
          queryKey: ["manufacturer", "meterModels"],
        });
      },
    }
  );
};
